import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTrip } from '../lib/tripContext.jsx';
import { useUser } from '../lib/userContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { getLeaderboard, getBacklog, dismissTransmission } from '../lib/api.js';
import Verdict from '../components/Verdict.jsx';
import Leaderboard from '../components/Leaderboard.jsx';
import ChallengePicker from '../components/ChallengePicker.jsx';
import PushSubscribe from '../components/PushSubscribe.jsx';
import QueuedSubmissions from '../components/QueuedSubmissions.jsx';
import SessionExpiredBanner from '../components/SessionExpiredBanner.jsx';
import BacklogCard from '../components/BacklogCard.jsx';

// Format today's day key in the trip's local timezone (kept for the
// header line — every other day-key concern is server-side now).
function todayInTz(timezone) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

export default function Wilman() {
  const { tripId, trip } = useTrip();
  const { activePlayerId } = useUser();
  const session = useSession();
  // Manual challenge-fire is the Senior Producer's job, not a player's.
  const canFireChallenges = session?.ok && session.role === 'spectator-judge';
  const tz = trip?.dates?.timezone || 'America/Juneau';

  const [backlog, setBacklog] = useState({
    open: [],
    completed: [],
    missed: [],
    counts: { open: 0, completed: 0, missed: 0 },
    trip_over: false,
    post_production_mode: false,
    post_production_deadline: null,
  });
  const [leaderboard, setLeaderboard] = useState(null);
  const [latestVerdict, setLatestVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    setSessionExpired(false);
    try {
      const [bl, lb] = await Promise.all([
        getBacklog({ tripId }),
        getLeaderboard({ tripId }),
      ]);
      setBacklog(bl);
      setLeaderboard(lb.leaderboard);
    } catch (err) {
      // 401 — iOS Safari ITP eviction or other session loss. Surface the
      // friendly re-sign-in banner instead of a raw error chip.
      if (err.status === 401) {
        setSessionExpired(true);
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleVerified = useCallback(
    ({ submission, leaderboard: lb }) => {
      setLatestVerdict(submission);
      if (lb) setLeaderboard(lb);
      // Re-fetch the backlog so the card moves OPEN → COMPLETED.
      refresh();
    },
    [refresh]
  );

  const handleFired = useCallback(() => {
    setLatestVerdict(null);
    refresh();
  }, [refresh]);

  const handleDismiss = useCallback(
    async (transmission) => {
      if (dismissing) return;
      setDismissing(true);
      try {
        await dismissTransmission({
          tripId,
          transmissionId: transmission.id,
          challengeId: transmission.challenge_id,
        });
        // Optimistic local update: drop from open immediately, then
        // refresh to reconcile with server-side dismissed list.
        setBacklog((prev) => ({
          ...prev,
          open: prev.open.filter((e) => e.transmission.id !== transmission.id),
          counts: { ...prev.counts, open: Math.max(0, prev.counts.open - 1) },
        }));
        refresh();
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setDismissing(false);
      }
    },
    [dismissing, tripId, refresh]
  );

  const open = backlog.open;
  const completed = backlog.completed;
  const missed = backlog.missed;
  const allEmpty = open.length === 0 && completed.length === 0 && missed.length === 0;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-5 p-4">
      <header className="flex items-baseline justify-between gap-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
            transmissions · {todayInTz(tz)}
          </p>
          <h1 className="mt-1 font-serif text-2xl">Wilman</h1>
        </div>
        {canFireChallenges && <ChallengePicker tripId={tripId} onFired={handleFired} />}
      </header>

      {sessionExpired && <SessionExpiredBanner />}

      {backlog.post_production_mode && (
        <PostProductionBanner deadline={backlog.post_production_deadline} />
      )}

      {backlog.post_production_mode && (
        <Link
          to="/trip-roll"
          className="card flex items-center justify-between gap-3 border-l-4 border-l-copper px-4 py-3 transition hover:bg-bg-tertiary"
        >
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-copper-dark">
              best of · for the montage
            </p>
            <p className="mt-1 text-sm text-fg-primary">
              Upload your best photos and video clips — they feed the trip
              montage. ≤ 60s and ≤ 100 MB per video.
            </p>
          </div>
          <span aria-hidden className="shrink-0 self-center font-mono text-[10px] uppercase tracking-wider text-action">
            open ›
          </span>
        </Link>
      )}

      {error && !sessionExpired && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}

      {loading && allEmpty && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …tuning in
        </p>
      )}

      {!loading && allEmpty && !sessionExpired && (
        <div className="card px-4 py-6 text-center text-sm text-fg-secondary">
          <p className="font-serif italic">Mr. Wilman has nothing to say at the moment.</p>
          {canFireChallenges ? (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              Tap &ldquo;fire challenge&rdquo; to issue one manually.
            </p>
          ) : (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              Transmissions arrive on their own schedule. Stay frosty.
            </p>
          )}
        </div>
      )}

      {open.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-secondary">
            open challenges · {open.length}
          </h2>
          <ul className="space-y-3">
            {open.map((entry) => (
              <BacklogCard
                key={entry.transmission.id}
                entry={entry}
                kind="open"
                tripId={tripId}
                playerId={activePlayerId}
                onVerified={handleVerified}
                onDismiss={handleDismiss}
                dismissing={dismissing}
              />
            ))}
          </ul>
        </section>
      )}

      {!loading && open.length === 0 && (completed.length > 0 || missed.length > 0) && (
        <div className="card border-success/40 bg-success/5 px-4 py-3 text-center text-sm text-fg-secondary">
          <p className="font-serif italic">All caught up. Mr. Wilman will have more to say.</p>
        </div>
      )}

      {latestVerdict && <Verdict submission={latestVerdict} />}

      {completed.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-secondary">
            completed · {completed.length}
          </h2>
          <ul className="space-y-2">
            {completed.map((entry) => (
              <BacklogCard
                key={`c-${entry.submission?.id || entry.transmission.id}`}
                entry={entry}
                kind="completed"
              />
            ))}
          </ul>
        </section>
      )}

      {missed.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-secondary">
            missed · {missed.length}
          </h2>
          <ul className="space-y-2">
            {missed.map((entry) => (
              <BacklogCard
                key={`m-${entry.transmission.id}`}
                entry={entry}
                kind="missed"
              />
            ))}
          </ul>
        </section>
      )}

      <QueuedSubmissions />

      {leaderboard && (
        <Leaderboard leaderboard={leaderboard} players={trip?.players || []} activePlayerId={activePlayerId} />
      )}

      <PushSubscribe />
    </div>
  );
}

// Banner that shows on the Wilman tab when the trip has ended but the
// post-production catchup window is still open. Players can submit to
// any unattempted challenge until the deadline. Renders the deadline
// in the device's local timezone (with explicit zone abbreviation) so
// nobody guesses at "midnight" semantics.
function PostProductionBanner({ deadline }) {
  if (!deadline) return null;
  let label = deadline;
  try {
    const dt = new Date(deadline);
    label = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(dt);
  } catch {
    /* fall back to raw ISO */
  }
  return (
    <div className="card border-copper/40 bg-copper/5 px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-copper-dark">
        post-production mode
      </p>
      <p className="mt-1 text-sm text-fg-primary">
        Submit to any missed challenge until <strong>{label}</strong>. After
        that, the books close.
      </p>
    </div>
  );
}
